import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonKPICard,
} from "../ui/skeleton";
import { Dialog, DialogContent } from "../ui/dialog";
import { Plus } from "lucide-react";
import { useContextDetection } from "../../hooks/useContextDetection";
import { UnifiedControlService } from "../../services/unifiedServices";
import { BusinessUnitService } from "../../services/businessUnitService";
import CreateControlForm from "../Controls/CreateControlForm";
import ControlDetails from "../Controls/ControlDetails";
import ControlTable from "../Controls/ControlTable";
import ControlStats from "../Controls/ControlStats";
import PageHeader from "../Common/PageHeader";
import { getApiUrl } from "../../config/api";

const UnifiedControlsPage = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const {
    contextId,
    contextType,
    organizationId,
    businessUnitId,
    isOrganizationContext,
    canManage,
  } = useContextDetection();

  const [contextData, setContextData] = useState(null);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateControlOpen, setIsCreateControlOpen] = useState(false);
  const [isEditControlOpen, setIsEditControlOpen] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);

  // Get context-specific data (organization or business unit details)
  const fetchContextData = async () => {
    if (isOrganizationContext && organizationId) {
      try {
        const response = await fetch(
          getApiUrl(`organizations/${organizationId}`),
          {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch organization");
        }

        const data = await response.json();
        setContextData(data);
      } catch (err) {
        console.error("Error fetching organization data:", err);
      }
    } else if (!isOrganizationContext && businessUnitId) {
      try {
        const data = await BusinessUnitService.getBusinessUnitDetails(
          businessUnitId,
          getAccessTokenSilently
        );
        setContextData(data);
      } catch (err) {
        console.error("Error fetching business unit data:", err);
      }
    }
  };

  const fetchControls = async () => {
    if (!contextId) return;

    try {
      setLoading(true);
      const data = await UnifiedControlService.getControls(
        contextType,
        contextId,
        organizationId || contextData?.organization_id,
        getAccessTokenSilently
      );
      setControls(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching controls:", err);
      setError(err.message);
      setControls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contextId) {
      fetchContextData();
    }
  }, [contextId, isOrganizationContext]);

  useEffect(() => {
    if (contextId && contextData) {
      fetchControls();
    }
  }, [contextId, contextData]);

  const handleControlCreated = () => {
    setIsCreateControlOpen(false);
    fetchControls();
  };

  const handleEditControl = (control) => {
    setSelectedControl(control);
    setIsEditControlOpen(true);
  };

  const handleControlUpdated = () => {
    setIsEditControlOpen(false);
    setSelectedControl(null);
    fetchControls();
  };

  const handleDeleteControl = async (control) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the control: "${control.name}"?`
      )
    ) {
      return;
    }

    try {
      await UnifiedControlService.deleteControl(
        control.id,
        getAccessTokenSilently
      );
      fetchControls();
    } catch (err) {
      console.error("Error deleting control:", err);
      setError("Failed to delete control. Please try again.");
    }
  };

  // Generate context-aware page title
  const getPageTitle = () => {
    return "Controls";
  };

  const getPageSubtitle = () => {
    if (isOrganizationContext) {
      return "Manage controls for this organization";
    } else {
      return "Manage controls for this business unit";
    }
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-32" />
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Table Skeleton */}
        <SkeletonCard>
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <SkeletonTable rows={8} columns={6} />
          </div>
        </SkeletonCard>
      </div>
    );
  }

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <PageHeader
          pageName={getPageTitle()}
          entityName={contextData?.name}
          entityType={isOrganizationContext ? "organization" : "business unit"}
        />
        {canManage && (
          <Button
            size="sm"
            onClick={() => setIsCreateControlOpen(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Control
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-8">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Control Stats */}
      <ControlStats controls={controls} />

      {/* Controls Table */}
      <ControlTable
        controls={controls}
        canManage={canManage}
        onEditControl={handleEditControl}
        onDeleteControl={handleDeleteControl}
        onCreateControl={() => setIsCreateControlOpen(true)}
      />

      {/* Create Control Dialog */}
      <Dialog open={isCreateControlOpen} onOpenChange={setIsCreateControlOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <CreateControlForm
            organizationId={organizationId || contextData?.organization_id}
            businessUnitId={isOrganizationContext ? null : businessUnitId}
            onClose={() => setIsCreateControlOpen(false)}
            onControlCreated={handleControlCreated}
            getAccessTokenSilently={getAccessTokenSilently}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Control Dialog */}
      {selectedControl && (
        <ControlDetails
          control={selectedControl}
          onClose={() => {
            setIsEditControlOpen(false);
            setSelectedControl(null);
          }}
          onControlUpdated={handleControlUpdated}
          canEdit={canManage}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      )}
    </div>
  );
};

export default UnifiedControlsPage;
